import type Luminary from '@lumen5/luminary';
import type { Scene } from '@lumen5/luminary';
import { TEMPLATE_COLOR_VAR_PREFIX, TEMPLATE_FONT_VAR_PREFIX, VideoData } from '@lumen5/luminary';

import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { htmlCompositionSkill } from 'js/src/agent/skills/definitions/htmlComposition';
import { getHtmlDesignSkills } from 'js/src/agent/html-design/skills/skillRegistry';
import { LoadSkillTool } from 'js/src/agent/tools/LoadSkillTool';
import { ReviewSceneImagesTool } from 'js/src/agent/tools/ReviewSceneImagesTool';
import type { Tool } from 'js/src/agent/tools/Tool';
import type { ConversationMessage } from 'js/src/agent/types';
import { getTemplateFontFamilies } from 'js/src/agent/video-state/serialization/videoSerializer';
import {
    getHtmlDesignPlan,
    getHtmlDesignSystem,
} from 'js/src/data/html-design/htmlDesignSelectors';
import type { AppDispatch, GetRootState, RootState } from 'js/src/libs/RootState';

import { EditHtmlAssetTool } from './EditHtmlAssetTool';
import { isLayeredGraphicScene } from './layeredGraphic';
import { ReadHtmlAssetTool } from './ReadHtmlAssetTool';
import { formatSceneNarration } from './sceneNarration';
import { WriteHtmlAssetTool } from './WriteHtmlAssetTool';
import { WritePlanTool } from './WritePlanTool';

/**
 * The child that designs one graphic layered over an existing scene. It sees nothing of the
 * conversation that started it, so everything it needs is in this prompt and the packet it is
 * handed — the layering contract lives here and nowhere else the authoring model reads.
 */
export function layeredGraphicSystemPrompt(): string {
    return `You design one motion graphic layered over a scene of a Lumen5 video. What the scene already
shows — footage, a speaker, a stock design — stays on screen beneath your graphic; your graphic adds
to it. You are handed a brief, the scene's narration and a still of the scene. Work on that scene
only.

1. Look at the still: where the subject sits — a speaker's face, the focal point of the footage, the
   design's own copy — and where the clear space is.
2. Plan with ${TOOL_NAMES.writePlan}, writing the clear zone you chose into the plan's \`layout\`.
3. Load the techniques the plan's approval assigns, then author with ${TOOL_NAMES.writeHtmlAsset}
   and fix what lint reports.
4. Check the result over the real scene with ${TOOL_NAMES.reviewSceneImages} and fix what it finds.
5. Reply in one or two sentences: what the graphic shows, where, and on which words.

On a revision the packet carries the current composition: change what the brief asks and keep the
rest, with ${TOOL_NAMES.editHtmlAsset}.

## The layering contract
- **The page is transparent** — the scene beneath is its background.
- **Keep off the subject**, in the clear space the still shows.
- **Text sits on a plate.** Copy drawn straight onto the scene beneath has no knowable contrast.
- **Leave the caption band clear** when captions are on.

${htmlCompositionSkill.instructions}`;
}

function formatPalette(luminary: Luminary): string {
    const colors = (luminary.template?.colors ?? []).map(
        (slot, index) => `${TEMPLATE_COLOR_VAR_PREFIX}${index}: ${slot.color}`,
    );
    const fonts = getTemplateFontFamilies(luminary).map(
        (family, index) => `${TEMPLATE_FONT_VAR_PREFIX}${index}: ${family}`,
    );
    return [...colors, ...fonts].join('\n') || '(none)';
}

function otherLayeredGraphics(luminary: Luminary, scene: Scene): string[] {
    return luminary.scenes
        .filter(
            (other) =>
                other.id !== scene.id &&
                isLayeredGraphicScene(other) &&
                other.getAssetsByType(VideoData.HTML_ASSET_TYPE).some((asset) => asset.html),
        )
        .map((other) => other.id);
}

/** Everything the child needs to design the graphic, read from the video rather than briefed. */
export function layeredGraphicPacket(
    luminary: Luminary,
    state: RootState,
    scene: Scene,
    brief: string,
): string {
    const [graphic] = scene.getAssetsByType(VideoData.HTML_ASSET_TYPE);
    const current = graphic?.html ?? '';
    const plan = getHtmlDesignPlan(state, scene.id);
    const designSystem = getHtmlDesignSystem(state);
    const siblings = otherLayeredGraphics(luminary, scene);
    const captionsOn = Boolean(scene.sceneGroup?.captions?.visible);
    const sections = [
        `## Brief\n${brief}`,
        `## Scene\nscene_id: ${scene.id}\nDuration: ${(scene.duration / 1000).toFixed(2)}s\n` +
            `Captions: ${captionsOn ? 'on — keep the bottom band clear' : 'off'}`,
        `## Narration\n${formatSceneNarration(scene, state)}`,
        `## Template tokens\n${formatPalette(luminary)}`,
        designSystem
            ? `## Design system\nOpen the composition with it, before your own styles:\n<style>\n${designSystem.css}\n</style>`
            : '## Design system\nNone yet: use the template tokens.',
        siblings.length > 0
            ? `## Other layered graphics in this video\nScenes ${siblings.join(', ')}. Read them with ` +
              `${TOOL_NAMES.readHtmlAsset} and carry their look across.`
            : '',
        current
            ? `## Current composition (revision)\n${plan ? `Plan: ${JSON.stringify(plan)}\n\n` : ''}${current}`
            : '## Current composition\nEmpty: this is a new graphic.',
        '## The scene as it is now',
    ];
    return sections.filter(Boolean).join('\n\n');
}

/** The design tools the child works with; `SingleSceneScope` keeps their edits on its scene. */
export function layeredGraphicTools(
    luminary: Luminary,
    dispatch: AppDispatch,
    getState: GetRootState,
    getConversationMessages: () => ConversationMessage[],
): Tool[] {
    const args = { luminary, dispatch, getState };
    return [
        new WritePlanTool({ ...args, getConversationMessages, authorsLayeredGraphics: true }),
        new WriteHtmlAssetTool({ ...args, authorsLayeredGraphics: true }),
        new EditHtmlAssetTool({ ...args, authorsLayeredGraphics: true }),
        new ReadHtmlAssetTool(args),
        new ReviewSceneImagesTool(args),
        new LoadSkillTool({ luminary, dispatch, skills: getHtmlDesignSkills() }),
    ];
}
